// Google Identity Services (GIS) によるGmail送信用アクセストークンの取得モジュール。
//
// 設計方針:
// - initTokenClient は一度だけ生成（コールバックは初期化時固定のため、
//   モジュールレベルの pending 解決子へ橋渡しする）。
// - 同意ポップアップの競合を避けるため、リクエストは直列化する。
// - アカウント指定は GIS 正式パラメータの login_hint で行い、hd で
//   Workspace ドメインに限定する（誤アカウント選択の予防）。
// - 取得したトークンは必ず userinfo で「どのアカウントのものか」を確認し、
//   送信ユーザと一致した場合のみ返す・キャッシュする。
// - 不一致の場合はアカウント選択を強制して1回だけ再取得し、なお不一致なら
//   AccountMismatchError で中断する（フェイルクローズ＝誤アカウント送信を構造的に防ぐ）。
import {
  CLIENT_ID,
  SCOPES,
  WORKSPACE_DOMAIN,
} from "../../constants/googleAuth";
import {
  AccountMismatchError,
  TokenVerificationError,
  isCacheUsable,
  normalizeEmail,
} from "./gmailTokenCore";

const GSI_TIMEOUT_MS = 10000;
const USERINFO_ENDPOINT = "https://openidconnect.googleapis.com/v1/userinfo";

let tokenClient = null;
let pending = null; // { resolve, reject } — 直列化済みなので同時に1件のみ
let queue = Promise.resolve();

let cache = null; // { token, expiry, email } — email はトークンの実アカウント

// GSI クライアントスクリプト (public/index.html で読み込み) の準備を待つ。
const waitForGsi = () =>
  new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) {
        resolve();
        return;
      }
      if (Date.now() - start > GSI_TIMEOUT_MS) {
        reject(
          new Error("Google Identity Services の読み込みに失敗しました")
        );
        return;
      }
      setTimeout(tick, 100);
    };
    tick();
  });

const ensureTokenClient = async () => {
  if (tokenClient) return tokenClient;
  await waitForGsi();
  tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: SCOPES,
    hd: WORKSPACE_DOMAIN,
    // コールバックは初期化時に固定し、現在の pending を解決する。
    // 検証（誰のトークンか）は requestVerifiedGmailToken 側で行うため、
    // ここではトークンレスポンスをそのまま渡す。
    callback: (response) => {
      const p = pending;
      pending = null;
      if (!p) return;
      if (response && response.access_token) {
        p.resolve(response);
      } else {
        p.reject(
          new Error((response && response.error) || "トークンの取得に失敗しました")
        );
      }
    },
    // ポップアップが閉じられた等の復帰不能エラー。
    error_callback: (err) => {
      const p = pending;
      pending = null;
      if (!p) return;
      p.reject(
        err instanceof Error
          ? err
          : new Error((err && err.type) || "トークン取得が中断されました")
      );
    },
  });
  return tokenClient;
};

// GIS へ1回だけトークンを要求する。resolve 値はトークンレスポンス全体。
const requestOnce = ({ senderEmail, prompt }) =>
  new Promise((resolve, reject) => {
    ensureTokenClient()
      .then((client) => {
        pending = { resolve, reject };
        try {
          client.requestAccessToken({
            prompt: prompt || "",
            login_hint: senderEmail || "",
          });
        } catch (e) {
          pending = null;
          reject(e);
        }
      })
      .catch(reject);
  });

// 直列化して実行（前のリクエストの成否に関わらず次を実行できるようにする）。
const enqueue = (run) => {
  const result = queue.then(run, run);
  queue = result.catch(() => {});
  return result;
};

/** トークンの実アカウント（メールアドレス）を userinfo で取得する。 */
const fetchTokenAccountEmail = async (accessToken) => {
  let info;
  try {
    const response = await fetch(USERINFO_ENDPOINT, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
      throw new Error(`userinfo HTTP ${response.status}`);
    }
    info = await response.json();
  } catch (e) {
    throw new TokenVerificationError(e);
  }
  if (!info || !info.email) {
    throw new TokenVerificationError(
      new Error("userinfo 応答に email が含まれていません")
    );
  }
  return info.email;
};

// トークンの実アカウントを確認し、送信ユーザと一致すればキャッシュして返す。
const verifyAndCache = async (tokenResponse, senderEmail) => {
  const actualEmail = await fetchTokenAccountEmail(tokenResponse.access_token);
  if (normalizeEmail(actualEmail) !== normalizeEmail(senderEmail)) {
    return { matched: false, actualEmail };
  }
  const expiresInMs = (Number(tokenResponse.expires_in) || 3600) * 1000;
  cache = {
    token: tokenResponse.access_token,
    expiry: Date.now() + expiresInMs,
    email: normalizeEmail(actualEmail),
  };
  return { matched: true, token: tokenResponse.access_token };
};

/**
 * 送信ユーザ本人のアカウントであることを検証済みの Gmail 送信用トークンを取得する。
 *
 * 取得フロー:
 * 1. 送信ユーザ一致のキャッシュがあれば即返す
 * 2. login_hint 付きで取得（既定はサイレント。必要時のみ Google が UI を表示）
 * 3. userinfo で実アカウントを確認。不一致ならアカウント選択を強制して1回だけ再取得
 * 4. なお不一致なら AccountMismatchError（呼び出し側は送信を中止すること）
 *
 * @param {string} senderEmail 送信ユーザ（= メールの From）のメールアドレス
 * @param {object} [opts]
 * @param {string} [opts.prompt] 初回要求の prompt を強制する場合に指定
 *   （例: 401 後の再認可は "consent"）。省略時はサイレント優先
 * @returns {Promise<string>} access_token（実アカウント検証済み）
 * @throws {AccountMismatchError} アカウントを選び直しても送信ユーザと一致しない
 * @throws {TokenVerificationError} 実アカウントの確認自体に失敗した
 */
export const requestVerifiedGmailToken = (senderEmail, { prompt } = {}) => {
  const normalized = normalizeEmail(senderEmail);
  if (!normalized) {
    return Promise.reject(
      new Error("送信ユーザのメールアドレスが取得できないため送信できません")
    );
  }
  if (!prompt && isCacheUsable(cache, normalized, Date.now())) {
    return Promise.resolve(cache.token);
  }
  return enqueue(async () => {
    // 待機中に先行リクエストがキャッシュを満たした場合は再利用する。
    if (!prompt && isCacheUsable(cache, normalized, Date.now())) {
      return cache.token;
    }
    cache = null;
    const first = await requestOnce({ senderEmail, prompt: prompt || "" });
    const firstResult = await verifyAndCache(first, senderEmail);
    if (firstResult.matched) return firstResult.token;

    // 不一致: アカウント選択を強制して1回だけ取り直す。
    const second = await requestOnce({
      senderEmail,
      prompt: "select_account",
    });
    const secondResult = await verifyAndCache(second, senderEmail);
    if (secondResult.matched) return secondResult.token;

    throw new AccountMismatchError(secondResult.actualEmail, senderEmail);
  });
};

/**
 * モーダル表示時などに事前にトークンクライアントを初期化しておく。
 * これによりボタンクリック時の requestAccessToken がユーザージェスチャ内で
 * 同期的に呼ばれ、ポップアップブロックを回避できる。
 */
export const preloadGmailAuth = () => {
  ensureTokenClient().catch(() => {
    // 事前ロードの失敗は無視（送信時に再試行・明示エラー化する）。
  });
};

/** キャッシュ済みトークンを破棄する（サインアウト時など）。 */
export const clearGmailAccessToken = () => {
  cache = null;
};
